import React, { useEffect, useState } from 'react';
import { Btn, Modal, Input, Textarea } from '../shared/UI';
import { whatsappNumber } from '../../utils/helpers';
import { useT } from '../../i18n';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

// One place to get a document to the client: public link, WhatsApp, or email with the PDF attached.
// onDocChange receives the updated document whenever the server changes it (link created, email sent).
export default function SendShareModal({ open, onClose, doc, company, onDocChange, show }) {
  const { t } = useT();
  const { user } = useAuth();
  const [url, setUrl] = useState(doc.shareUrl || '');
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({ to: '', cc: '', subject: '', message: '', attachPdf: true, includeLink: true });

  const client = doc.clientSnapshot || {};
  const typeLabel = t(`docType.${doc.type}`);
  const vars = {
    name: client.name || client.company || '', type: typeLabel, n: doc.docNumber,
    company: company.name || user?.name || '', sender: company.signatoryName || company.name || user?.name || '',
  };

  // Fresh defaults each time the modal opens (the client or language may have changed)
  useEffect(() => {
    if (!open) return;
    setUrl(doc.shareUrl || '');
    // Chasing an overdue invoice reads differently from sending a new one
    const chasing = doc.type === 'invoice' && doc.status === 'overdue';
    setForm({
      to: client.email || '', cc: '',
      subject: t(chasing ? 'share.reminderSubject' : 'share.defaultSubject', vars),
      message: t(chasing ? 'share.reminderMessage' : 'share.defaultMessage', vars),
      attachPdf: true, includeLink: true,
    });
  }, [open]); // deliberately only on open — typing must not be overwritten

  const ensureLink = async () => {
    if (url) return url;
    const { data } = await api.post(`/documents/${doc._id}/share`);
    setUrl(data.url);
    onDocChange({ shareToken: data.shareToken, shareUrl: data.url });
    return data.url;
  };

  const createLink = async () => {
    setBusy('link');
    try { await ensureLink(); }
    catch (err) { show(err.response?.data?.message || t('share.linkFailed'), 'error'); }
    finally { setBusy(''); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); show(t('share.copied')); }
    catch { window.prompt(t('share.linkTitle'), url); }
  };

  const disable = async () => {
    if (!window.confirm(t('share.disableConfirm'))) return;
    setBusy('link');
    try {
      await api.delete(`/documents/${doc._id}/share`);
      setUrl('');
      onDocChange({ shareToken: null, shareUrl: null });
      show(t('share.disabled'));
    } catch (err) { show(err.response?.data?.message || t('share.linkFailed'), 'error'); }
    finally { setBusy(''); }
  };

  const whatsapp = async () => {
    setBusy('wa');
    // Open the tab synchronously — a window opened after an await is treated as a popup and blocked
    const tab = window.open('', '_blank');
    try {
      const link = await ensureLink();
      const text = encodeURIComponent(t('share.whatsappText', { ...vars, url: link }).replace(/ {2,}/g, ' '));
      const target = `https://wa.me/${whatsappNumber(client.phone, doc.currency)}?text=${text}`;
      if (tab) tab.location.href = target; else window.location.href = target;
      // Sharing it is sending it
      if (doc.status === 'draft') {
        const { data } = await api.patch(`/documents/${doc._id}/status`, { status: 'sent' });
        onDocChange(data);
      }
    } catch (err) {
      if (tab) tab.close();
      show(err.response?.data?.message || t('share.linkFailed'), 'error');
    } finally { setBusy(''); }
  };

  const sendEmail = async e => {
    e.preventDefault();
    setBusy('email');
    try {
      const { data } = await api.post(`/documents/${doc._id}/send`, form);
      onDocChange(data);
      setUrl(data.shareUrl || url);
      show(t('share.sent', { to: form.to }));
      onClose();
    } catch (err) { show(err.response?.data?.message || t('share.sendFailed'), 'error'); }
    finally { setBusy(''); }
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <Modal open={open} onClose={onClose} title={t('share.title', { n: doc.docNumber })} width={600}>
      {/* Link + WhatsApp */}
      <p style={sectionTitle}>🔗 {t('share.linkTitle')}</p>
      <p style={help}>{t('share.linkHelp')}</p>
      {url ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input readOnly value={url} onFocus={e => e.target.select()} dir="ltr" style={{
            flex: 1, minWidth: 200, padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8,
            fontSize: 13, fontFamily: 'inherit', background: 'var(--input-bg)', color: 'var(--text)',
          }} />
          <Btn variant="primary" onClick={copy}>{t('share.copy')}</Btn>
          <a href={`${url}?owner=1`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}><Btn variant="ghost">{t('share.open')}</Btn></a>
        </div>
      ) : (
        <Btn variant="primary" onClick={createLink} disabled={busy === 'link'}>{t('share.createLink')}</Btn>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <Btn onClick={whatsapp} disabled={busy === 'wa'} style={{ background: '#25D366', color: '#fff' }}>💬 {t('share.whatsapp')}</Btn>
        {url && <Btn variant="ghost" onClick={disable} disabled={busy === 'link'}>{t('share.disable')}</Btn>}
      </div>

      {/* Email */}
      <form onSubmit={sendEmail} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
        <p style={{ ...sectionTitle, marginBottom: 0 }}>✉ {t('share.emailTitle')}</p>
        <Input label={t('share.to')} type="text" inputMode="email" required dir="ltr" placeholder="client@example.com"
          value={form.to} onChange={set('to')} />
        <Input label={t('share.cc')} type="text" dir="ltr" placeholder={t('share.ccPh')} value={form.cc} onChange={set('cc')} />
        <Input label={t('share.subject')} required maxLength={200} value={form.subject} onChange={set('subject')} />
        <Textarea label={t('share.message')} rows={6} maxLength={5000} value={form.message} onChange={set('message')} />
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13, color: 'var(--text)' }}>
          <label style={check}><input type="checkbox" checked={form.attachPdf} onChange={set('attachPdf')} /> {t('share.attachPdf')}</label>
          <label style={check}><input type="checkbox" checked={form.includeLink} onChange={set('includeLink')} /> {t('share.includeLink')}</label>
        </div>
        <p style={{ ...help, marginBottom: 0 }}>{t('share.replyNote', { email: company.email || user?.email || '' })}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn variant="accent" type="submit" disabled={busy === 'email'}>{busy === 'email' ? t('share.sending') : t('share.sendEmail')}</Btn>
        </div>
      </form>
    </Modal>
  );
}

const sectionTitle = { fontSize: 14, fontWeight: 700, color: 'var(--heading)', marginBottom: 4 };
const help = { fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 };
const check = { display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' };
